import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import ttAPI from '../../api/ttAPI';
import {
  Grid, TextField, InputAdornment, IconButton, Chip, Typography,
  Button, CircularProgress, Box, Menu, MenuItem, Select, FormControl, Paper,
  InputLabel, FormControlLabel, Switch, NativeSelect, Tooltip
} from "@material-ui/core";
import { MuiPickersUtilsProvider, DatePicker as MaterialDatePicker } from '@material-ui/pickers';
import MUIDataTable from "mui-datatables";
import MultiSelectAutoComplete from '../../components/MultiSelectAutoComplete/MultiSelectAutoComplete';
import { createMuiTheme, MuiThemeProvider } from '@material-ui/core/styles';
import debounce from 'lodash.debounce';
import PageTitle from "../../components/PageTitle";
import { sendNotification, useUserState } from "../../context/UserContext";
import useStyles from './styles';
import moment from 'moment';
import config from '../../config';
import DateFnsUtils from '@date-io/date-fns';
import {
  Search as SearchIcon,
  Clear as ClearIcon,
  MoreVert as MoreVertIcon,
  VpnKey as VpnKeyIcon,
  TrackChanges as TrackChangesIcon,
} from '@material-ui/icons';
import classnames from "classnames";
import './Retests.css';

const EditableCell = React.memo(function EditableCell({
  value,
  width,
  onSave,
  placeholder,
}) {
  const [draft, setDraft] = useState(value || '');

  useEffect(() => {
    setDraft(value || '');
  }, [value]);

  return (
    <div
      style={width}
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
      onFocus={(e) => e.stopPropagation()}
    >
      <TextField
        value={draft}
        fullWidth
        variant="outlined"
        size="small"
        placeholder={placeholder || ''}
        label={placeholder || ''}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={() => onSave(draft)}
        InputProps={{ style: { fontSize: 14 } }}
      />
    </div>
  );
});

const EmailDraftField = React.memo(function EmailDraftField({
  value,
  onSave,
}) {
  const [draft, setDraft] = useState(value || '');

  useEffect(() => {
    setDraft(value || '');
  }, [value]);

  return (
    <TextField
      label="From Email"
      value={draft}
      onChange={(e) => {
        const v = e.target.value;
        setDraft(v);
        onSave(v.trim());   // keep parent state synced
      }}
      variant="outlined"
      fullWidth
    />

  );
});

const TokenChip = ({ label }) => (
  <span
    style={{
      display: 'inline-block',
      padding: '2px 8px',
      margin: '0 4px',
      background: '#eef3f8',
      border: '1px solid #cfd8dc',
      borderRadius: 12,
      fontSize: 12,
      fontWeight: 500,
      color: '#455a64',
      whiteSpace: 'normal',
      maxWidth: '100%',
      verticalAlign: 'middle'
    }}
  >
    {label}
  </span>
);

const ClickToEditNumberCell = React.memo(function ClickToEditNumberCell({
    value,
    rowIndex,
    editingRowIndex,
    setEditingRowIndex,
    onSave,
  }) {
    const [draft, setDraft] = useState(value ?? '');

    useEffect(() => {
      setDraft(value ?? '');
    }, [value, editingRowIndex, rowIndex]);

    const isEditing = editingRowIndex === rowIndex;

    if (!isEditing) {
      return (
        <div
          onClick={(e) => {
            e.stopPropagation();
            setEditingRowIndex(rowIndex);
          }}
          onMouseDown={(e) => e.stopPropagation()}
          style={{
            cursor: 'pointer',
            minWidth: 32,
            padding: '6px 0',
            color: '#555',
            transition: 'color 0.15s ease'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.color = '#1976d2'; // nice MUI blue vibe
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.color = '#555';
          }}
        >
          {value ?? ''}
        </div>
      );
    }

    return (
      <div
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
        onFocus={(e) => e.stopPropagation()}
        style={{ minWidth: 70 }}
      >
        <TextField
          value={draft}
          autoFocus
          variant="standard"
          size="small"
          type="number"
          onChange={(e) => setDraft(e.target.value)}
          onBlur={() => {
            onSave(draft);
            setEditingRowIndex(null);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              onSave(draft);
              setEditingRowIndex(null);
            }
            if (e.key === 'Escape') {
              setDraft(value ?? '');
              setEditingRowIndex(null);
            }
          }}
          InputProps={{
            style: { fontSize: 14, width: 70 }
          }}
        />
      </div>
    );
  });

const RetestEmail = () => {
  const classes = useStyles();
  var { user_email } = useUserState();
  const topRef = useRef(null);

  const [sortByColumn, setSortByColumn] = useState('email_total_assets');
  const [sortDirection, setSortDirection] = useState('desc');

  const [isRange, setIsRange] = useState(false);
  const [retestRegionsOptions, setRetestRegionsOptions] = useState([]);
  const [cachedSelectedRetestRegions, setCachedSelectedRetestRegions] = useState([]);

  const [selectedAssetType, setSelectedAssetType] = useState('ALL');

  const [editingAssetsRowIndex, setEditingAssetsRowIndex] = useState(null);

  const [data, setData] = useState([]);
  const [count, setCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  const [page, setPage] = useState(0);
  const [offset, setOffset] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(100);

  const [key, setKey] = useState('');
  const delayedOnSearchKey = useRef(
    debounce(q => {
      setPage(0);
      setOffset(0);
      setKey(q);
    }, 500)
  ).current;

  const [from, setFrom] = useState();
  const [to, setTo] = useState();

  const [isSendingEmails, setIsSendingEmails] = useState(false);

  const [selectedRegions, setSelectedRegions] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);
  const [messageType, setMessageType] = useState('REMINDER');
  const [selectedEmailActivity, setSelectedEmailActivity] = useState('');
  const [emailDate, setEmailDate] = useState(null);

  const [retestStatusOptions, setRetestStatusOptions] = useState([]);
  const [selectedStatuses, setSelectedStatuses] = useState([]);
  const [cachedSelectedRetestStatuses, setCachedSelectedRetestStatuses] = useState([]);
  const [statusFilterInitialized, setStatusFilterInitialized] = useState(false);

  const RETEST_EMAIL_FILTERS_KEY = 'retest_email_filters';
  const [searchText, setSearchText] = useState('');
  const [body, setBody] = useState(getDefaultBodyByMessageType('REMINDER'));
  const [reminderBodyParts, setReminderBodyParts] = useState(() =>
    splitReminderBody(getDefaultBodyByMessageType('REMINDER'))
  );
  const [bookingDateTime, setBookingDateTime] = useState('');
  const RETEST_EMAIL_FROM_CACHE_KEY = 'retest-email-from';
  const [fromEmail, setFromEmail] = useState(() => {
    return localStorage.getItem(RETEST_EMAIL_FROM_CACHE_KEY) || user_email || '';
  })



  const activityTextMap = {
    'NEEDS REMINDER': 'needing a reminder',
    'REMINDER SENT': 'with reminders sent',
    'NEEDS BOOKING CONFIRMATION': 'needing booking confirmation',
    'BOOKING CONFIRMATION SENT': 'with booking confirmations sent',
    'QUOTE SENT': 'with quotes sent',
    'RESCHEDULE SENT': 'with reschedule emails sent'
  };

  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [menuRow, setMenuRow] = useState(null);

  const emailSectionRef = useRef(null);

  const scrollToEmailSection = () => {
    if (emailSectionRef.current) {
      emailSectionRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
        top: 0
      });
    }
  };

  const handleOpenMenu = (event, row) => {
    setMenuAnchorEl(event.currentTarget);
    setMenuRow(row);
  };

  const handleCloseMenu = () => {
    setMenuAnchorEl(null);
    setMenuRow(null);
  };

  const handleOpenRetest = () => {
    if (!menuRow) return;

    window.open(
        `${config.adminApi}/#/app/retest/book/${menuRow.id}`,
        '_blank'
    );
    handleCloseMenu();
  };

  const handleSaveEmailTotalAssets = useCallback(async (rowIndex, newValue) => {
    const row = data[rowIndex];
    if (!row) return;

    const raw = String(newValue ?? '').trim();
    const parsed = parseInt(raw, 10);

    const value = raw === '' || isNaN(parsed) || parsed < 0
      ? (row.email_total_assets ?? row.site_total_assets ?? 0)
      : parsed;

    const oldValue = row.email_total_assets ?? row.site_total_assets ?? 0;
    if (oldValue === value) return;

    const updated = [...data];
    updated[rowIndex] = {
      ...updated[rowIndex],
      email_total_assets: value,
    };
    setData(updated);

    const response = await ttAPI.updateRetestEmailTotalAssets(row.id, value);

    if (response !== undefined && response.data !== undefined &&
        response.data[0] !== undefined) {
      if (response.data[0].ok) {
        sendNotification('success', 'Assets updated successfully.', classes);
      } else {
        sendNotification('error', `Failed to update assets for retest ${row.id}.`, classes);
      }
    } else {
      sendNotification('error', `Failed to update assets for retest ${row.id}.`, classes);
    }
  }, [data, classes]);

  useEffect(() => {
    let mounted = true;

    const fetchRetestStatuses = async () => {
      const response = await ttAPI.getAllUIRetestStatus();

      if (!mounted || !response || response.length === 0) return;

      setRetestStatusOptions(response);

      const cached = getStoredRetestEmailFilters();

      if (cached && Array.isArray(cached.selectedStatuses) && cached.selectedStatuses.length > 0) {
        setSelectedStatuses(cached.selectedStatuses);
        setCachedSelectedRetestStatuses(
          response.filter(option => cached.selectedStatuses.includes(option.id))
        );
      } else {
        const defaultInProgress = response.filter(
          option => option.category_name === 'In Progress'
        );

        setSelectedStatuses(defaultInProgress.map(option => option.id));
        setCachedSelectedRetestStatuses(defaultInProgress);
      }

      setStatusFilterInitialized(true);
    };

    fetchRetestStatuses();

    return () => {
      mounted = false;
    };
  }, []);


  useEffect(() => {
      const cached = getStoredRetestEmailFilters();

      if (cached) {
        setKey(cached.key || '');
        setSearchText(cached.key || '');
        setPage(cached.page || 0);
        setRowsPerPage(cached.rowsPerPage || 100);
        setOffset((cached.page || 0) * (cached.rowsPerPage || 100));
        setIsRange(!!cached.isRange);
        setSelectedRegions(cached.selectedRegions || []);
        setCachedSelectedRetestRegions(cached.cachedSelectedRetestRegions || []);
        setFrom(cached.from ? new Date(cached.from) : getDefaultFrom());
        setTo(cached.to ? new Date(cached.to) : getDefaultTo());

        const cachedActivity = cached.selectedEmailActivity || '';
        setSelectedEmailActivity(cachedActivity);
        setEmailDate(cached.emailDate ? new Date(cached.emailDate) : null);
        setSelectedStatuses(cached.selectedStatuses || []);
        setSelectedAssetType(cached.selectedAssetType || 'ALL');
      } else {
        setFrom(getDefaultFrom());
        setTo(getDefaultTo());
        setSelectedAssetType('ALL');
      }
    }, []);

    useEffect(() => {
      if (!fromEmail && user_email) {
        setFromEmail(user_email);
      }
    }, [user_email]);

    useEffect(() => {
        if (!from || !to) return;

        saveRetestEmailFilters({
          key,
          page,
          rowsPerPage,
          isRange,
          from: from ? from.toISOString() : null,
          to: to ? to.toISOString() : null,
          selectedRegions,
          cachedSelectedRetestRegions,
          selectedEmailActivity,
          emailDate: emailDate ? emailDate.toISOString() : null,
          selectedStatuses,
          selectedAssetType
        });
    }, [key, page, rowsPerPage, isRange, from, to, selectedRegions, cachedSelectedRetestRegions, selectedEmailActivity, emailDate, selectedStatuses, selectedAssetType]);

  useEffect(() => {
    let mounted = true;

    const fetchData = async () => {
      if (from === undefined || to === undefined || from === null || to === null || !statusFilterInitialized) {
        return;
      }

      setIsLoading(true);

      let fromDate = null;
      let toDate = null;

      if (!isNaN(from.getTime())) {
        let [date, month, year] = from.toLocaleDateString('en-NZ').split("/");
        fromDate = `${year}-${month}-${date}`;
      }

      if (!isNaN(to.getTime())) {
        let [date, month, year] = to.toLocaleDateString('en-NZ').split("/");
        toDate = `${year}-${month}-${date}`;
      }

      let emailDateValue = null;
      if (emailDate && !isNaN(emailDate.getTime())) {
        let [date, month, year] = emailDate.toLocaleDateString('en-NZ').split("/");
        emailDateValue = `${year}-${month}-${date}`;
      }

      const response = await ttAPI.getRetestsForEmail(
        key,
        offset,
        rowsPerPage,
        selectedRegions,
        selectedStatuses,
        selectedAssetType,
        fromDate,
        toDate,
        selectedEmailActivity,
        emailDateValue,
        sortByColumn,
        sortDirection
      );

      if (mounted) {
        if (response !== null && response !== undefined) {
          setData(response.retests === null || response.retests === undefined ? [] : response.retests);
          setCount(response.total_retests || 0);
        } else {
          setData([]);
          setCount(0);
        }
        setIsLoading(false);
      }
    };

    fetchData();
    return () => mounted = false;
    }, [
      key,
      offset,
      rowsPerPage,
      selectedRegions,
      selectedStatuses,
      selectedAssetType,
      from,
      to,
      selectedEmailActivity,
      emailDate,
      sortByColumn,
      sortDirection,
      statusFilterInitialized
    ]);

  useEffect(() => {
    let mounted = true;

    const fetchRetestRegions = async () => {
      const response = await ttAPI.getRegions();
      if (mounted && response !== null && response !== undefined) {
        setRetestRegionsOptions(response);
      }
    };

    fetchRetestRegions();

    return () => mounted = false;
  }, []);

  useEffect(() => {
    const defaultBody = getDefaultBodyByMessageType(messageType);
    setBody(defaultBody);

    if (messageType === 'REMINDER') {
      setReminderBodyParts(splitReminderBody(defaultBody));
    }
  }, [messageType]);

  function getDefaultBodyByMessageType(type) {

      switch (type) {

        case 'REMINDER':
          return `Hope you are well and safe.

We are emailing to let you know that [COMPANY_AND_SITES] has [TOTAL_TAGS] due for [SERVICE_RETESTING].

We would like to send a technician to complete this work on [BOOKING_DATE_TIME]. If this is acceptable you can reply to this email or call to confirm. If this date does not suit, please contact us to arrange an alternative date.

Looking forward to hearing from you.`;


        case 'BOOKING_CONFIRMATION':
          return `Your booking for electrical testing has been confirmed.

Our technician will attend at the scheduled time. If there are any site access requirements or changes before the visit, please let us know.`;

        case 'QUOTE_REQUEST':
          return `Thank you for your enquiry regarding electrical testing.

We would be happy to provide a quotation based on your site requirements. Please let us know the site details and approximate number of assets so we can prepare this for you.`;

        case 'RESCHEDULE':
          return `We understand that the scheduled testing may need to be moved.

Please let us know a suitable alternative date and we will do our best to arrange a new time that works for your site.`;

        default:
          return '';
      }

    }

    const handleReminderBodyPartChange = useCallback((field, value) => {
      setReminderBodyParts(prev => {
        const next = {
          ...prev,
          [field]: value
        };

        setBody(buildReminderBodyFromParts(next));
        return next;
      });
    }, []);

    function splitReminderBody(template) {
      const defaults = {
        intro: 'Hope you are well and safe.',
        bookingStart: 'We would like to send a technician to complete this work on',
        bookingEnd: 'If this is acceptable you can reply to this email or call to confirm. If this date does not suit, please contact us to arrange an alternative date.',
        closing: 'Looking forward to hearing from you.'
      };

      if (!template) return defaults;

      const paragraphs = template.split(/\n\s*\n/).map(p => p.trim());

      const bookingParagraph = paragraphs[2] || '';
      const bookingParts = bookingParagraph.split('[BOOKING_DATE_TIME]');

      return {
        intro: paragraphs[0] || defaults.intro,
        bookingStart: (bookingParts[0] || defaults.bookingStart).trim(),
        bookingEnd: (bookingParts[1] || defaults.bookingEnd).trim(),
        closing: paragraphs[3] || defaults.closing
      };
    }

    function buildReminderBodyFromParts(parts) {
      return `${parts.intro}

    We are emailing to let you know that [COMPANY_AND_SITES] has [TOTAL_TAGS] due for [SERVICE_RETESTING].

    ${parts.bookingStart} [BOOKING_DATE_TIME] ${parts.bookingEnd}

    ${parts.closing}`;
    }

  function buildBookingDateTimeLabel(group) {
  if (!group || !group.notes || !group.notes.trim()) {
    return 'Booking Date & Time';
  }

  return group.notes.trim();
}

  const changePage = async (p, rpp) => {
    setPage(p);
    setRowsPerPage(rpp);
    setOffset(p * rpp);
    setIsLoading(true);
  };

  const handleBlurEmail = useCallback(async (rowIndex, newValue) => {
    const row = data[rowIndex];
    if (!row) return;

    const trimmedNewValue = (newValue || '').trim();
    const oldValue = (row.last_used_email || '').trim();

    if (oldValue === trimmedNewValue) return;

    const updated = [...data];
    updated[rowIndex] = {
      ...updated[rowIndex],
      last_used_email: trimmedNewValue,
    };
    setData(updated);

    const response = await ttAPI.updateRetestEmailFields(
      row.site_id,
      trimmedNewValue,
      (row.email_greeting || '').trim()
    );

    if (response !== null) {
      sendNotification('success', 'Email saved.', classes);
    } else {
      sendNotification('error', 'Failed to save email.', classes);
    }
  }, [data, classes]);

  const handleBlurGreeting = useCallback(async (rowIndex, newValue) => {
    const row = data[rowIndex];
    if (!row) return;

    const trimmedNewValue = (newValue || '').trim();
    const oldValue = (row.email_greeting || '').trim();

    if (oldValue === trimmedNewValue) return;

    const updated = [...data];
    updated[rowIndex] = {
      ...updated[rowIndex],
      email_greeting: trimmedNewValue,
    };
    setData(updated);

    const response = await ttAPI.updateRetestEmailFields(
      row.site_id,
      (row.last_used_email || '').trim(),
      trimmedNewValue
    );

    if (response !== null) {
      sendNotification('success', 'Greeting saved.', classes);
    } else {
      sendNotification('error', 'Failed to save greeting.', classes);
    }
  }, [data, classes]);

  function getDefaultFrom() {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth() + 1, 1);
  }

  function getDefaultTo() {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth() + 2, 0);
  }

  function onSelectRetestRegions(values) {
    setPage(0);
    setOffset(0);
    setSelectedRegions(values.map(({ id }) => id));
    setCachedSelectedRetestRegions(values);
  }

  function getStoredRetestEmailFilters() {
    try {
      const raw = localStorage.getItem(RETEST_EMAIL_FILTERS_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch (e) {
      return null;
    }
  }

  function saveRetestEmailFilters(filters) {
    localStorage.setItem(RETEST_EMAIL_FILTERS_KEY, JSON.stringify(filters));
  }

  function onSelectRetestStatuses(values) {
    setPage(0);
    setOffset(0);
    setSelectedStatuses(values.map(({ id }) => id));
    setCachedSelectedRetestStatuses(values);
  }

  function handleChangeSelectAssetType(event) {
    setPage(0);
    setOffset(0);
    setSelectedAssetType(event.target.value);
  }

  const handleSendEmails = async () => {
    if (isSendingEmails) return; // prevent double clicking

    const ids = selectedRows.map(index => data[index].id).filter(Boolean);;

    if (ids.length === 0) {
      sendNotification('warning', 'Please select at least one retest.', classes);
      return;
    }

    setIsSendingEmails(true);

    try {
      const response = await ttAPI.sendRetestEmail(ids, messageType, body, fromEmail);

      if (response !== null && response.success) {
        sendNotification(
          'success',
          (response.message || 'Retest email batch queued.') + ' Refresh the page shortly to view updated statuses.',
          classes
        );
      } else {
        sendNotification('error', 'Failed to queue retest emails.', classes);
      }
    } catch (e) {
      sendNotification('error', 'Failed to queue retest emails.', classes);
    } finally {
      setIsSendingEmails(false);
    }
  };

  const handleSaveFromEmail = useCallback((newValue) => {
    const cleaned = (newValue || '').trim();
    const finalValue = cleaned || user_email || '';

    if (finalValue !== fromEmail) {
      setFromEmail(finalValue);
      localStorage.setItem(RETEST_EMAIL_FROM_CACHE_KEY, finalValue);
    }
  }, [fromEmail, user_email]);


  const muiTheme = useMemo(() => createMuiTheme({
    palette: {
      primary: { main: '#FFCA06' }
    },
    overrides: {
      MuiTableCell: {
        root: {
          padding: '0px 12px',
          verticalAlign: 'middle'
        }
      },
      MUIDataTableBodyCell: {
        root: {
          verticalAlign: 'middle',
          paddingTop: '8px',
          paddingBottom: '8px'
        }
      },
      MUIDataTableHeadCell: {
        fixedHeader: {
          verticalAlign: 'middle',
          fontWeight: 700,
          textAlign: 'left',
          fontSize: '14px'
        }
      },
      MUIDataTableToolbarSelect: {
        root: { display: 'none' }
      },
    }
  }), []);

  const columns = useMemo(() => [
    { label: 'ID', name: 'id', options: { sort: false, display: false, download: false, filter: false, viewColumns: false } },
    {
      label: 'Customer / Site',
      name: 'customer_name',
      options: {
        filter: true,
        sort: false,
        setCellProps: () => ({
          style: {
            verticalAlign: 'middle'
          }
        }),
        customBodyRenderLite: (dataIndex) => {
          const row = data[dataIndex];

          return (
              <span>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>

                  {row.key_customer && (
                    <Tooltip title="Key Customer">
                      <VpnKeyIcon style={{ fontSize: 18, color: '#d4a017' }} />
                    </Tooltip>
                  )}

                  {row.kpi_customer && (
                    <Tooltip title="KPI Customer">
                      <TrackChangesIcon style={{ fontSize: 18, color: '#d4a017'}} />
                    </Tooltip>
                  )}

                  <strong>{row.customer_name}</strong>
                </span>

                <br/>

                <span style={{ color: '#666', fontSize: '0.9em' }}>
                  {row.site_name}
                </span>
              </span>
            );
        }
      }
    },
    { label: 'Expiry Date', name: 'expiry_month',
      options: { filter: false, sort: true,
        setCellProps: () => ({
          style: {
            verticalAlign: 'middle'
          }
        }),
      }
    },
    {
      label: 'Last Tested',
      name: 'last_tested',
      options: {
        filter: false,
        sort: false,
        setCellProps: () => ({
          style: {
            verticalAlign: 'middle'
          }
        }),
        customBodyRender: (value, tableMeta) => {
          const row = data[tableMeta.rowIndex];
          return (
            <span style={row && row.last_tested_close ? { color: '#d32f2f', fontWeight: 600 } : {}}>
              {value}
            </span>
          );
        }
      }
    },
    {
      label: 'Contact Details',
      name: 'site_contact',
      options: {
        filter: false,
        sort: false,
        setCellProps: () => ({
          style: {
            verticalAlign: 'middle'
          }
        }),
        customBodyRender: (value, tableMeta) => {
          const row = data[tableMeta.rowIndex];
          return (
            <div style={{ minWidth: 220, maxWidth: 260, whiteSpace: 'normal', wordBreak: 'break-word' }}>
              {row.site_contact && <div>{row.site_contact}</div>}
              {row.site_phone && (
                <div style={{ fontSize: '0.85em', color: '#666' }}>{row.site_phone}</div>
              )}
              {row.site_email && (
                <div style={{ fontSize: '0.85em', color: '#666', wordBreak: 'break-word' }}>
                  {row.site_email}
                </div>
              )}
            </div>
          );
        }
      }
    },
    {
      label: 'Email Contact',
      name: 'last_used_email',
      options: {
        filter: false,
        sort: false,
        setCellProps: () => ({
          style: {
            verticalAlign: 'middle',
            minWidth: '280px'
          }
        }),
        setCellHeaderProps: () => ({
          style: {
            minWidth: '280px'
          }
        }),
        customBodyRenderLite: (dataIndex) => {
          const row = data[dataIndex];
          if (!row) return null;

          return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <EditableCell
                value={row.last_used_email || ''}
                width={{ minWidth: 260, maxWidth: 320 }}
                placeholder="Send Email To"
                onSave={(newValue) => handleBlurEmail(dataIndex, newValue)}
              />

              <EditableCell
                value={row.email_greeting || ''}
                width={{ minWidth: 260, maxWidth: 320 }}
                placeholder="Greeting Name"
                onSave={(newValue) => handleBlurGreeting(dataIndex, newValue)}
              />
            </div>
          );
        }
      }
    },
    {
      label: 'Assets',
      name: 'email_total_assets',
      options: {
        filter: false,
        sort: true,
        setCellProps: () => ({
          style: {
            verticalAlign: 'middle',
            minWidth: '90px'
          }
        }),
        customBodyRenderLite: (dataIndex) => {
          const row = data[dataIndex];
          if (!row) return null;

          return (
            <ClickToEditNumberCell
              value={row.email_total_assets ?? row.site_total_assets ?? ''}
              rowIndex={dataIndex}
              editingRowIndex={editingAssetsRowIndex}
              setEditingRowIndex={setEditingAssetsRowIndex}
              onSave={(newValue) => handleSaveEmailTotalAssets(dataIndex, newValue)}
            />
          );
        }
      }
    },
    {
      label: 'Region',
      name: 'region_name',
      options: {
        filter: true,
        sort: false,
        setCellProps: () => ({
          style: { verticalAlign: 'middle' }
        }),
        customBodyRender: (value) => {
          if (!value) return '';

          let cleaned = value;

          // remove "Test and Tag"
          cleaned = cleaned.replace(/^Test and Tag\s*/i, '');

          // remove "Region"
          cleaned = cleaned.replace(/\s*Region$/i, '');


          return (
            <span style={{ fontWeight: 500 }}>
              {cleaned.trim()}
            </span>
          );
        }
      }
    },
    {
  label: 'Retest Info',
    name: 'status',
    options: {
      filter: false,
      sort: false,
      setCellProps: () => ({
        style: {
          verticalAlign: 'middle',
          minWidth: '150px'
        }
      }),
      customBodyRenderLite: (dataIndex) => {
        const row = data[dataIndex];

        const status = row.status || '';
        const reminded = row.date_reminded || '';
        const notes = row.notes || '';

        return (
          <span>
            <div style={{ fontWeight: 600 }}>{status}</div>

            {reminded && (
              <div style={{ fontSize: '0.9em', color: '#666' }}>
                Reminded {reminded}
              </div>
            )}

            {notes && (
              <div style={{ fontSize: '0.9em', marginTop: '2px' }}>
                {notes}
              </div>
            )}
          </span>
        );
      }
    }
  },
    {
      label: 'Last Msg',
      name: 'last_message_type',
      options: {
        filter: true,
        sort: false,
        setCellProps: () => ({
          style: {
            whiteSpace: 'normal',
            wordBreak: 'keep-all',
            overflowWrap: 'normal',
            minWidth: '90px',
            paddingRight: '12px',
            verticalAlign: 'middle'
          }
        }),
        setCellHeaderProps: () => ({
          style: { minWidth: '90px' }
        }),
        customBodyRender: (value) => {
          if (!value) return '';

          const cleaned = value.replace(/_/g, ' ');

          return (
            <span style={{ fontWeight: 500 }}>
              {cleaned}
            </span>
          );
        }
      }
    },
    {
      label: 'Last Email Date',
      name: 'last_sent_at',
      options: {
        filter: false,
        sort: true,
        setCellProps: () => ({
          style: { minWidth: '110px', verticalAlign: 'middle' }
        }),
        setCellHeaderProps: () => ({
          style: { minWidth: '110px' }
        }),
        customBodyRender: (value) => {
          if (!value) return '';

          const d = new Date(value);

          const date = d.toLocaleDateString('en-NZ', {
            day: '2-digit',
            month: 'short',
            year: 'numeric'
          });

          const time = d.toLocaleTimeString('en-NZ', {
            hour: '2-digit',
            minute: '2-digit'
          });

          return (
            <span>
              {date}
              <br/>
              <span style={{ color: '#666', fontSize: '0.9em' }}>{time}</span>
            </span>
          );
        }
      }
    },
    {
      label: 'Email Status',
      name: 'email_send_status',
      options: {
        filter: true,
        sort: false,
        setCellProps: () => ({
          style: {
            verticalAlign: 'middle'
          }
        }),
        customBodyRender: (value) => {
          if (value === 'FAILED') {
            return <span style={{ color: '#c62828', fontWeight: 600 }}>{value}</span>;
          }
          if (value === 'SENT') {
            return <span style={{ color: '#2e7d32', fontWeight: 600 }}>{value}</span>;
          }
          if (value === 'PENDING') {
            return <span style={{ color: '#ef6c00', fontWeight: 600 }}>{value}</span>;
          }
          return value || '';
        }
      }
    },
    {
      label: 'Action',
      name: 'id',
      options: {
        filter: false,
        sort: false,
        download: false,
        print: false,
        empty: true,
        setCellProps: () => ({
          style: {
            verticalAlign: 'middle'
          }
        }),
        customBodyRenderLite: (dataIndex) => {
          const row = data[dataIndex];

          return (
            <IconButton
              size="small"
              onClick={(event) => handleOpenMenu(event, row)}
            >
              <MoreVertIcon fontSize="small" />
            </IconButton>
          );
        }
      }
    }
  ], [data, handleBlurEmail, handleBlurGreeting, handleSaveEmailTotalAssets, editingAssetsRowIndex]);

  const handleRowSelectionChange = useCallback((currentRowsSelected, allRowsSelected) => {
      const nextSelectedRows = allRowsSelected.map(x => x.dataIndex);

      setSelectedRows(prev => {
        if (
          prev.length === nextSelectedRows.length &&
          prev.every((value, index) => value === nextSelectedRows[index])
        ) {
          return prev;
        }
        return nextSelectedRows;
      });
    }, []);

  const tableOptions = useMemo(() => ({
    fixedHeader: true,
    tableBodyHeight: '60vh',
    serverSide: true,
    selectableRows: 'multiple',
    selectableRowsOnClick: false,
    search: false,
    filter: false,
    download: false,
    print: false,
    viewColumns: false,
    count: count,
    page: page,
    rowsPerPage: rowsPerPage,
    rowsPerPageOptions: [10, 20, 50, 100, 500],
    textLabels: {
      body: {
        noMatch: isLoading ? <CircularProgress size={25} color={'inherit'} /> : 'Sorry, no matching records found.'
      }
    },
    rowsSelected: selectedRows,
    onRowSelectionChange: handleRowSelectionChange,
    onChangePage: () => {},
    onChangeRowsPerPage: () => {},
    onTableChange: (action, tableState) => {
      switch (action) {
        case "changePage":
          changePage(tableState.page, tableState.rowsPerPage);
          break;
        case "changeRowsPerPage":
          changePage(tableState.page, tableState.rowsPerPage);
          break;
        case "sort":
          setPage(0);
          setOffset(0);
          setSortByColumn(tableState.sortOrder.name);
          setSortDirection(tableState.sortOrder.direction);
          break;
        default:
          break;
      }
    },
    sortOrder: {
      name: sortByColumn,
      direction: sortDirection
    },
    setRowProps: (row, dataIndex) => {
      const rowData = data[dataIndex];

      if (!rowData) return {};

      if (!rowData.last_used_email || !rowData.email_greeting) {
        return {
          style: { backgroundColor: '#fff8e1' }
        };
      }

      return {};
    },
  }), [count, page, rowsPerPage, isLoading, handleRowSelectionChange, data]);

  const selectedRetests = useMemo(
    () => selectedRows.map(index => data[index]).filter(Boolean),
    [selectedRows, data]
  );

  const groupedPreviews = useMemo(() => {
    if (selectedRetests.length === 0) return [];

    const groups = new Map();

    selectedRetests.forEach((row) => {
      const customer = (row.customer_name || '').trim();
      const email = (row.last_used_email || '').trim();

      const key = `${customer}|||${email}`;

      if (!groups.has(key)) {
        groups.set(key, {
          customer,
          email,
          names: [],
          sites: [],
          notes: '',
          total: 0,
          rowCount: 0,
        });
      }

      const group = groups.get(key);

      // 👇 add names
      const name = (row.email_greeting || '').trim();
      if (name && !group.names.includes(name)) {
        group.names.push(name);
      }

      if (row.site_name && !group.sites.includes(row.site_name)) {
        group.sites.push(row.site_name);
      }

      const note = (row.notes || '').trim();
      if (note && !group.notes) {
        group.notes = note;
      }

      group.total += Number(row.email_total_assets ?? row.site_total_assets ?? 0);
      group.rowCount += 1;
    });

    return Array.from(groups.values());
  }, [selectedRetests]);

  const singleGroupedPreview = useMemo(() => {
    if (groupedPreviews.length !== 1) return null;
    return groupedPreviews[0];
  }, [groupedPreviews]);

  function buildCompanyAndSitesLabel(group) {
    if (!group) return 'Company / Site(s)';

    if (!group.sites || group.sites.length === 0) {
      return group.customer || 'Company / Site(s)';
    }

    if (group.sites.length === 1) {
      return `${group.customer} ${group.sites[0]}`.trim();
    }

    return `${group.customer} (${group.sites.length} sites)`.trim();
  }

  function getGreetingLine() {
    if (singleGroupedPreview && singleGroupedPreview.names && singleGroupedPreview.names.length === 1) {
      const name = singleGroupedPreview.names[0];

      if (name && name.trim() !== '') {
        return `Hello ${name},`;
      }
    }

    return 'Hello [GREETING NAME],';
  }

  function getClosingLine() {
    return `Kind regards,
      TESTntel Scheduling Team`;
  }

  function buildSummaryLine(row) {
    const customer = row.customer_name || ' - ';
    const site = row.site_name || '';
    const due = row.expiry_month || '';
    const assets = row.email_total_assets ?? row.site_total_assets ?? '';
    const greeting = row.email_greeting || '[Contact Name NOT set]';
    const email = row.last_used_email || '[Contact Email NOT set]';
    const notes = (row.notes || '').trim();

    const notesPart = notes ? ` | Booking: ${notes}` : '';

    return `${customer}  ${site} (${due}, Assets: ${assets}) - ${greeting} (${email})${notesPart}`;
  }

  const summaryLines = useMemo(
    () => selectedRetests.map(buildSummaryLine),
    [selectedRetests]
  );

  const scrollToTop = () => {
    if (topRef.current) {
      topRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    }
  };

  const getFilterSentence = () => {
    let sentence = selectedEmailActivity ? 'Retests' : 'All retests';

    if (selectedEmailActivity) {
      const activity = selectedEmailActivity.toUpperCase();
      const formatted = emailDate ? moment(emailDate).format('D MMM') : null;

      if (activity === 'NEEDS REMINDER') {
        sentence += formatted
          ? ` needing a reminder (no reminder sent since ${formatted})`
          : ' needing a reminder';
      } else if (activity === 'NEEDS BOOKING CONFIRMATION') {
        sentence += formatted
          ? ` needing booking confirmation (no booking confirmation sent since ${formatted})`
          : ' needing booking confirmation';
      } else {
        const activityText =
          activityTextMap[activity] || selectedEmailActivity.toLowerCase();

        sentence += ` ${activityText}`;

        if (formatted) {
          sentence += ` since ${formatted}`;
        }
      }
    }

    return sentence;
  };

  function getEmailActivitySuffix(activity, formattedDate) {
    const key = (activity || '').toUpperCase();

    switch (key) {
      case 'NEEDS REMINDER':
        return formattedDate
          ? `(no reminder sent since ${formattedDate})`
          : '(all time)';

      case 'NEEDS BOOKING CONFIRMATION':
        return formattedDate
          ? `(no booking confirmation sent since ${formattedDate})`
          : '(all time)';

      case 'REMINDER SENT':
      case 'BOOKING CONFIRMATION SENT':
      case 'QUOTE SENT':
      case 'RESCHEDULE SENT':
        return formattedDate ? `since ${formattedDate}` : '(all time)';

      default:
        return '(all time)';
    }
  }

  const renderEmailProgressSentence = () => {
    const activity = (selectedEmailActivity || '').toUpperCase();

    const needsText =
      activity === 'NEEDS REMINDER'
        ? 'no reminder sent since'
        : activity === 'NEEDS BOOKING CONFIRMATION'
        ? 'no booking confirmation sent since'
        : null;

    const sentText =
      activity === 'REMINDER SENT' ||
      activity === 'BOOKING CONFIRMATION SENT' ||
      activity === 'QUOTE SENT' ||
      activity === 'RESCHEDULE SENT'
        ? 'since'
        : null;

    return (
      <Box
        display="flex"
        flexWrap="wrap"
        alignItems="center"
        style={{ gap: 8 }}
      >
        <Typography variant="body2" component="span" style={{ color: '#555' }}>
          Show retests
        </Typography>

        <Box style={{ minWidth: 260 }}>
          <FormControl fullWidth>
            <Select
              value={selectedEmailActivity}
              displayEmpty
              onChange={(e) => {
                setPage(0);
                setOffset(0);
                setSelectedEmailActivity(e.target.value);
              }}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="Needs Reminder">Needs Reminder</MenuItem>
              <MenuItem value="Needs Booking Confirmation">Needs Booking Confirmation</MenuItem>
              <MenuItem value="Reminder Sent">Reminder Sent</MenuItem>
              <MenuItem value="Booking Confirmation Sent">Booking Confirmation Sent</MenuItem>
              <MenuItem value="Quote Sent">Quote Sent</MenuItem>
              <MenuItem value="Reschedule Sent">Reschedule Sent</MenuItem>
            </Select>
          </FormControl>
        </Box>

        {selectedEmailActivity && needsText && (
          <>
            <Typography variant="body2" component="span" style={{ color: '#555' }}>
              ({needsText}
            </Typography>

            <Box display="flex" alignItems="center" style={{ gap: 4 }}>
              <Box style={{ width: 170 }}>
                <MuiPickersUtilsProvider utils={DateFnsUtils}>
                  <MaterialDatePicker
                    fullWidth
                    variant="inline"
                    value={emailDate || null}
                    onChange={(date) => {
                      setPage(0);
                      setOffset(0);
                      setEmailDate(date);
                    }}
                    format="dd/MM/yyyy"
                    InputProps={{
                      placeholder: 'All time'
                    }}
                  />
                </MuiPickersUtilsProvider>
              </Box>

              {emailDate && (
                <IconButton
                  size="small"
                  onClick={() => {
                    setPage(0);
                    setOffset(0);
                    setEmailDate(null);
                  }}
                >
                  <ClearIcon fontSize="small" />
                </IconButton>
              )}
            </Box>

            <Typography variant="body2" component="span" style={{ color: '#555' }}>
              )
            </Typography>
          </>
        )}

        {selectedEmailActivity && sentText && (
            <>
              <Typography variant="body2" component="span" style={{ color: '#555' }}>
                {sentText}
              </Typography>

              <Box display="flex" alignItems="center" style={{ gap: 4 }}>
                <Box style={{ width: 170 }}>
                  <MuiPickersUtilsProvider utils={DateFnsUtils}>
                    <MaterialDatePicker
                      fullWidth
                      variant="inline"
                      value={emailDate || null}
                      onChange={(date) => {
                        setPage(0);
                        setOffset(0);
                        setEmailDate(date);
                      }}
                      format="dd/MM/yyyy"
                      InputProps={{
                        placeholder: 'All time'
                      }}
                    />
                  </MuiPickersUtilsProvider>
                </Box>

                {emailDate && (
                  <IconButton
                    size="small"
                    onClick={() => {
                      setPage(0);
                      setOffset(0);
                      setEmailDate(null);
                    }}
                  >
                    <ClearIcon fontSize="small" />
                  </IconButton>
                )}
              </Box>
            </>
          )}

        {!selectedEmailActivity && (
          <Typography variant="body2" component="span" style={{ color: '#555' }}>
            (all time)
          </Typography>
        )}
      </Box>
    );
  };


  return (
    <>
    <Menu
      anchorEl={menuAnchorEl}
      keepMounted
      open={Boolean(menuAnchorEl)}
      onClose={handleCloseMenu}
      >
      <MenuItem onClick={handleOpenRetest}>Open Retest</MenuItem>
      </Menu>
      <div ref={topRef}></div>
      <PageTitle title="Retest Reminders" />

      <Grid container spacing={4}>
        <Grid item xs={12}>
        <div className="retest_range_filter">
          <Box mb={2}>
            <Paper elevation={0} style={{ padding: 20 }}>
              <Box
                display="flex"
                flexWrap="wrap"
                alignItems="flex-end"
                style={{ gap: 16 }}
              >
                <Box style={{ flex: '2 1 520px', minWidth: 300 }}>
                  <TextField
                    fullWidth
                    value={searchText}
                    onChange={(e) => {
                      setSearchText(e.target.value);
                      delayedOnSearchKey(e.target.value);
                    }}
                    label="Search customer/site/contact/phone/email"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label="clear search"
                            onClick={() => {
                              setPage(0);
                              setOffset(0);
                              setKey('');
                              setSearchText('');
                            }}
                            onMouseDown={(e) => e.preventDefault()}
                          >
                            {key ? <ClearIcon /> : null}
                          </IconButton>
                        </InputAdornment>
                      )
                    }}
                  />
                </Box>

                <Box
                  display="flex"
                  flexWrap="wrap"
                  alignItems="flex-end"
                  style={{ flex: '1 1 520px', minWidth: 460, gap: 16 }}
                >
                  <Box style={{ flex: '1 1 220px', minWidth: 200 }}>
                    <MuiPickersUtilsProvider utils={DateFnsUtils}>
                      <MaterialDatePicker
                        fullWidth
                        variant="inline"
                        views={["year", "month"]}
                        openTo="month"
                        label={isRange ? "Retest Expires From" : "Retest Expires In"}
                        value={from || null}
                        onChange={(date) => {
                          if (!date) return;
                          setPage(0);
                          setOffset(0);

                          const start = new Date(date.getFullYear(), date.getMonth(), 1);
                          setFrom(start);

                          if (!isRange) {
                            setTo(new Date(date.getFullYear(), date.getMonth() + 1, 0));
                          } else if (to && to < start) {
                            setTo(new Date(date.getFullYear(), date.getMonth() + 1, 0));
                          }
                        }}
                        format="MMM yyyy"
                      />
                    </MuiPickersUtilsProvider>
                  </Box>

                  {isRange && (
                    <Box style={{ flex: '1 1 220px', minWidth: 200 }}>
                      <MuiPickersUtilsProvider utils={DateFnsUtils}>
                        <MaterialDatePicker
                          fullWidth
                          variant="inline"
                          views={["year", "month"]}
                          openTo="month"
                          label="Retest Expires To"
                          value={to || null}
                          onChange={(date) => {
                            if (!date) return;
                            setPage(0);
                            setOffset(0);
                            setTo(new Date(date.getFullYear(), date.getMonth() + 1, 0));
                          }}
                          minDate={from || undefined}
                          format="MMM yyyy"
                        />
                      </MuiPickersUtilsProvider>
                    </Box>
                  )}

                  <Box style={{ flex: '0 0 auto', minWidth: 110, paddingBottom: 8 }}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={isRange}
                          name="isRange"
                          color="primary"
                          onChange={(event) => {
                            const checked = event.target.checked;
                            setPage(0);
                            setOffset(0);
                            setIsRange(checked);

                            if (!checked && from) {
                              setTo(new Date(from.getFullYear(), from.getMonth() + 1, 0));
                            } else if (checked && from && (!to || to < from)) {
                              setTo(new Date(from.getFullYear(), from.getMonth() + 1, 0));
                            }
                          }}
                        />
                      }
                      label="Range"
                    />
                  </Box>
                </Box>
              </Box>

              <Box mt={3}>
                <Box
                  display="flex"
                  flexWrap="wrap"
                  alignItems="flex-end"
                  style={{ gap: 16 }}
                >
                  {retestRegionsOptions !== null && retestRegionsOptions !== undefined &&
                    retestRegionsOptions.length > 0 && (
                      <Box style={{ flex: '2 1 420px', minWidth: 320 }}>
                        <MultiSelectAutoComplete
                          presetValue={cachedSelectedRetestRegions}
                          options={retestRegionsOptions}
                          label="Scheduling Zones/Regions"
                          idName="id"
                          displayName="name"
                          onSelect={onSelectRetestRegions}
                        />
                      </Box>
                  )}


                  {retestStatusOptions !== null && retestStatusOptions !== undefined &&
                    retestStatusOptions.length > 0 && (
                      <Box style={{ flex: '2 1 420px', minWidth: 320 }}>
                      <MultiSelectAutoComplete
                          presetValue={cachedSelectedRetestStatuses}
                          value={cachedSelectedRetestStatuses}
                          options={retestStatusOptions}
                          groupByName="category_name"
                          label="Retest Status"
                          idName="id"
                          displayName="name"
                          menuHeight="800px"
                          onSelect={onSelectRetestStatuses}
                          renderTags={(value, getTagProps) =>
                            value.map((option, index) => (
                              <Chip
                                classes={{
                                  root: classnames(classes.tag, {
                                    [classes.done]: option.category_name === 'Done',
                                    [classes.inProgress]: option.category_name === 'In Progress' && option.name !== 'NEW',
                                    [classes.actionRequired]: option.category_name === 'Action Required',
                                    [classes.onhold]: option.category_name === 'On-hold',
                                    [classes.archived]: option.category_name === 'Archived'
                                  })
                                }}
                                size="small"
                                label={`${option.name}`}
                                {...getTagProps({ index })}
                              />
                            ))
                          }
                          renderOption={(props, option) => {
                            return (
                              <span
                                title={props.description}
                                className={classnames({
                                  [classes.doneText]: props.category_name === 'Done',
                                  [classes.inProgressText]: props.category_name === 'In Progress' && props.name !== 'NEW',
                                  [classes.actionRequiredText]: props.category_name === 'Action Required',
                                  [classes.onholdText]: props.category_name === 'On-hold',
                                  [classes.archivedText]: props.category_name === 'Archived',
                                  [classes.archivedTextStrikeThrough]:
                                    props.category_name === 'On-hold' && props.name === 'CLOSED/NO NEED'
                                })}
                              >
                                {props.name}
                              </span>
                            );
                          }}
                          />
                      </Box>
                  )}

                  <Box style={{ flex: '1 1 220px', minWidth: 220 }}>
                    <FormControl fullWidth>
                      <InputLabel shrink htmlFor="asset_type_select">Asset Type</InputLabel>
                      <NativeSelect
                        id="asset_type_select"
                        value={selectedAssetType}
                        onChange={handleChangeSelectAssetType}
                      >
                        <option value="ALL">ALL</option>
                        <option value="GENERAL">GENERAL</option>
                        <option value="AED">AED</option>
                        <option value="EVSE">EVSE</option>
                        <option value="THERMAL">THERMAL</option>
                        <option value="TELTAG">TELTAG</option>
                      </NativeSelect>
                    </FormControl>
                  </Box>

                </Box>

                <Box mt={3}>
                  <Box
                    display="flex"
                    flexWrap="wrap"
                    alignItems="flex-end"
                    style={{ gap: 16 }}
                  >

                  <Box style={{ flex: '1 1 240px', minWidth: 240 }}>
                      <Paper
                        style={{
                          padding: 12,
                          background: '#f7f7f7',
                          border: '1px solid #e0e0e0',
                          borderRadius: 8
                        }}
                      >
                        {renderEmailProgressSentence()}
                      </Paper>
                  </Box>

                    </Box>

                  </Box>
              </Box>
            </Paper>
          </Box>
          </div>


          <MuiThemeProvider theme={muiTheme}>
            <MUIDataTable
              title={null}
              data={data}
              columns={columns}
              options={tableOptions}
            />
          </MuiThemeProvider>
        </Grid>


          <Grid item xs={12}>
        <div ref={emailSectionRef}>
            <Paper style={{ padding: 20 }}>
              <Grid container spacing={3}>
                <Grid item xs={12} md={3}>
                  <FormControl fullWidth>
                    <InputLabel>Message Type</InputLabel>
                    <Select
                      value={messageType}
                      onChange={(e) => setMessageType(e.target.value)}
                    >
                      <MenuItem value="REMINDER">Reminder</MenuItem>
                      <MenuItem value="BOOKING_CONFIRMATION">Booking Confirmation</MenuItem>
                      <MenuItem value="QUOTE_REQUEST">Quote Request</MenuItem>
                      <MenuItem value="RESCHEDULE">Reschedule</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={6}>
                  <EmailDraftField
                    value={fromEmail}
                    onSave={handleSaveFromEmail}
                  />
                </Grid>

                <Grid item xs={12}>
                  <div style={{ marginBottom: 10, fontWeight: 600 }}>
                    Summary ({selectedRetests.length})
                  </div>
                  <Paper style={{ padding: 12, background: '#f5f5f5' }}>
                    {groupedPreviews.length === 0 ? (
                      <Typography>No retests selected.</Typography>
                    ) : (
                      groupedPreviews.map((group, i) => (
                        <div
                          key={i}
                          style={{
                            padding: '8px 0',
                            borderBottom: i < groupedPreviews.length - 1 ? '1px solid #ddd' : 'none'
                          }}
                        >
                        <div style={{ fontWeight: 500 }}>
                          {group.customer || '[No customer]'}
                          {' | '}
                          {group.names.length > 0 ? group.names.join(', ') : '[No contact]'}
                          {' | '}
                          {group.email || '[No email]'}
                          {' | '}
                          {group.sites.length} site{group.sites.length !== 1 ? 's' : ''}
                          {' | '}
                          {group.total} tags
                          {group.notes ? ` | Booking: ${group.notes}` : ''}
                        </div>

                          <div style={{ fontSize: '0.9em', color: '#666', marginTop: 2 }}>
                            {group.sites.slice(0, 3).join(', ')}
                            {group.sites.length > 3 ? ` +${group.sites.length - 3} more` : ''}
                          </div>
                        </div>
                      ))
                    )}
                  </Paper>
                </Grid>

                <Grid item xs={12}>
                  <Paper style={{ padding: 20, background: '#fafafa' }} variant="outlined">

                    <div style={{ marginBottom: 16, whiteSpace: 'pre-line', fontWeight: 500 }}>
                      {getGreetingLine()}
                    </div>

                    {messageType === 'REMINDER' ? (
                        <div>
                          <TextField
                            label="Intro"
                            value={reminderBodyParts.intro}
                            onChange={(e) => handleReminderBodyPartChange('intro', e.target.value)}
                            multiline
                            rows={2}
                            variant="outlined"
                            fullWidth
                            style={{ marginBottom: 16 }}
                          />

                          <Paper
                            variant="outlined"
                            style={{
                              padding: 12,
                              marginBottom: 16,
                              background: '#f5f5f5',
                              lineHeight: 1.8
                            }}
                          >
                            We are emailing to let you know that
                            <TokenChip
                              label={
                                singleGroupedPreview
                                  ? buildCompanyAndSitesLabel(singleGroupedPreview)
                                  : 'Company / Site(s)'
                              }
                            />
                            has
                            <TokenChip
                              label={
                                singleGroupedPreview
                                  ? `${singleGroupedPreview.total} item${singleGroupedPreview.total === 1 ? '' : 's'}`
                                  : 'Total Tags'
                              }
                            />
                            <>
                              due for <TokenChip label="Service Retesting" />.
                            </>
                          </Paper>

                          <TextField
                            label="Booking sentence start"
                            value={reminderBodyParts.bookingStart}
                            onChange={(e) => handleReminderBodyPartChange('bookingStart', e.target.value)}
                            multiline
                            rows={2}
                            variant="outlined"
                            fullWidth
                            style={{ marginBottom: 16 }}
                          />

                          <Paper
                            variant="outlined"
                            style={{
                              padding: 12,
                              marginBottom: 16,
                              background: '#f5f5f5',
                              lineHeight: 1.8
                            }}
                          >
                            <TokenChip
                              label={
                                singleGroupedPreview
                                  ? buildBookingDateTimeLabel(singleGroupedPreview)
                                  : 'Booking Date & Time'
                              }
                            />
                          </Paper>

                          <TextField
                            label="Booking sentence ending"
                            value={reminderBodyParts.bookingEnd}
                            onChange={(e) => handleReminderBodyPartChange('bookingEnd', e.target.value)}
                            multiline
                            rows={3}
                            variant="outlined"
                            fullWidth
                            style={{ marginBottom: 16 }}
                          />

                          <TextField
                            label="Closing"
                            value={reminderBodyParts.closing}
                            onChange={(e) => handleReminderBodyPartChange('closing', e.target.value)}
                            multiline
                            rows={2}
                            variant="outlined"
                            fullWidth
                          />
                        </div>
                      ) : (
                        <TextField
                          label="Message"
                          value={body}
                          onChange={(e) => setBody(e.target.value)}
                          multiline
                          rows={8}
                          variant="outlined"
                          fullWidth
                        />
                      )}

                    <div style={{ marginTop: 16, whiteSpace: 'pre-line', fontWeight: 500 }}>
                      {getClosingLine()}
                    </div>

                  </Paper>
                </Grid>

                <Grid item xs={12}>
                <Button
                  onClick={handleSendEmails}
                  disabled={isSendingEmails || selectedRows.length === 0}
                  >
                  {isSendingEmails ? 'Sending...' : 'Send Email'}
                  </Button>
                </Grid>
              </Grid>
            </Paper>
        </div>
        </Grid>
      </Grid>

      {selectedRows.length > 0 && (
        <Paper
          elevation={3}
          style={{
            position: 'fixed',
            bottom: 16,
            left: 340,
            right: 200,
            zIndex: 1300,
            padding: '10px 16px',
            display: 'flex',
            alignItems: 'center',
            gap: 16
          }}
        >
        <Button
          onClick={scrollToEmailSection}
        >
          Review
        </Button>

        <Button
         size="small"
         onClick={scrollToTop}
       >
         ↑ Top
       </Button>

      <span>{selectedRows.length} selected</span>

        </Paper>
      )}
      <div style={{ paddingBottom: selectedRows.length > 0 ? 80 : 0 }}/>
    </>
  );
};

export default RetestEmail;
